<template>
  <!-- Keep teleport destinations mounted when navigating through pages without breadcrumbs. -->
  <nav v-show="crumbs.length > 1" class="app-breadcrumbs" :aria-label="t('navigation.breadcrumbs')">
    <div id="page-navigation-menu" class="app-breadcrumbs__menu" />
    <ol ref="trail">
      <li v-for="(crumb, index) in crumbs" :key="`${crumb.key}-${index}`">
        <v-icon v-if="index" icon="mdi-chevron-right" size="15" aria-hidden="true" />
        <SeasonEventSwitcher v-if="crumb.key === 'event'" :club-id="String(route.params.clubId)" :season-id="String(route.params.seasonId)" :current-event-id="String(route.params.tryoutId)" show-when-single>
          <template #activator="{ props: menuProps, open }">
            <BreadcrumbMenuTrigger :label="label(crumb)" :to="crumb.to" :menu-props="menuProps" :menu-label="t('shell.eventSwitcher.change')" @open="open" />
          </template>
        </SeasonEventSwitcher>
        <span v-else-if="index === crumbs.length - 1 && crumb.key !== 'event'" aria-current="page" :title="label(crumb)">{{ label(crumb) }}</span>
        <router-link v-else :aria-current="index === crumbs.length - 1 ? 'page' : undefined" :to="crumb.to" :title="label(crumb)">{{ label(crumb) }}</router-link>
      </li>
      <li id="page-navigation-context" class="app-breadcrumbs__context" />
    </ol>
  </nav>
</template>

<script setup lang="ts">
import { pluralizeModelNoun } from '@/utils/modelNouns'
import BreadcrumbMenuTrigger from './BreadcrumbMenuTrigger.vue'
import SeasonEventSwitcher from '@/components/SeasonEventSwitcher.vue'
import { useResizeObserver } from '@vueuse/core'
import { computed, nextTick, onScopeDispose, ref, watch } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'
import { useAuthStore } from '@/stores/auth'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useClubStore } from '@/stores/club'
import { breadcrumbsFor, type Breadcrumb } from './navigation'

const route = useRoute(), router = useRouter(), { t, te } = useI18n(), clubs = useClubStore()
// Observe the shared V2 query cache; page refetches/realtime invalidation refresh
// the title without another API request or a separate stale copy of event data.
const queryClient = useQueryClient(), auth = useAuthStore(), cacheRevision = ref(0)
const unsubscribe = queryClient.getQueryCache().subscribe(event => {
  if (['tryoutCapture', 'athleteJournal', 'tryoutContext', 'tryoutHome', 'sessionPlan', 'participantImportMetadata', 'tryoutParticipants', 'tryoutSetup'].includes(String(event.query.queryKey[0]))) cacheRevision.value++
})
onScopeDispose(unsubscribe)
const currentEvents = computed(() => {
  void cacheRevision.value
  const club = String(route.params.clubId || ''), season = String(route.params.seasonId || ''), eventId = String(route.params.tryoutId || '')
  if (!club || !season || !eventId) return []
  const keys = [
    ['tryoutCapture', club, season, eventId],
    ['participantImportMetadata', season, eventId, club],
    ['tryoutParticipants', club, season, eventId],
    ['tryoutSetup', season, eventId, club],
    ['athleteJournal', club, season, eventId, auth.user?.id || ''],
    ['tryoutContext', club, season, eventId],
    ['tryoutHome', season, eventId, club, auth.user?.details?.id || auth.user?.id || ''],
  ]
  return keys.map(queryKey => queryClient.getQueryCache().find({queryKey, exact:true}))
    .filter(query => query?.state.status === 'success')
    .sort((a,b) => (b?.state.dataUpdatedAt || 0) - (a?.state.dataUpdatedAt || 0))
    .map(query => (query?.state.data as {event?: {id?: string; title?: string; participantNoun?: string; participantPronoun?: string}} | undefined)?.event)
    .filter(event => event?.id === eventId)
})
const eventTitle = computed(() => currentEvents.value.find(event => event?.title)?.title || '')
const participantLabel = computed(() => {
  const event = currentEvents.value.find(event => event?.participantNoun || event?.participantPronoun)
  const noun = event?.participantNoun || event?.participantPronoun
  if (!noun) return t('navigation.participants')
  const plural = pluralizeModelNoun(noun)
  return plural.charAt(0).toUpperCase() + plural.slice(1)
})
const crumbs = computed(() => breadcrumbsFor(route, router.options.history.state.settingsReturnTo))
const trail = ref<HTMLOListElement | null>(null)
useResizeObserver(trail, () => { if (trail.value) trail.value.scrollLeft = trail.value.scrollWidth })
watch(() => route.fullPath, async () => {
  await nextTick()
  if (trail.value) trail.value.scrollLeft = trail.value.scrollWidth
}, { immediate: true })
function label(crumb: Breadcrumb) {
  if (crumb.key === 'sessionPlan') {
    void cacheRevision.value
    const data = queryClient.getQueryData<{ session?: { title?: string } }>(['sessionPlan', String(route.params.clubId), String(route.params.seasonId), String(route.params.tryoutId), String(route.params.sessionId), auth.user?.id || ''])
    return data?.session?.title || t('navigation.sessionPlan')
  }
  if (crumb.key === 'event') return eventTitle.value || t('navigation.event')
  if (crumb.key === 'club') return clubs.data[String(route.params.clubId)]?.name || t('navigation.club')
  if (crumb.key === 'participants') return participantLabel.value
  if (crumb.key === 'importParticipants') return t('navigation.importParticipants', { participant: participantLabel.value })
  if (crumb.tab) return te(crumb.tab) ? t(crumb.tab, { participant: participantLabel.value }) : t('navigation.section')
  return t(`navigation.${crumb.key}`)
}
</script>

<style scoped>
.app-breadcrumbs { display: flex; align-items: center; gap: 12px; padding: 6px 18px; min-height: 48px; background: linear-gradient(110deg, var(--cs-soft-white), #faf7ff 75%, #fff4eb); border-bottom: 1px solid var(--cs-border); color: var(--cs-muted); font-size: .8rem; }
.app-breadcrumbs__context:empty, .app-breadcrumbs__menu:empty, .app-breadcrumbs__switcher:empty { display: none; }
.app-breadcrumbs__menu { flex: 0 0 auto; }
.app-breadcrumbs__switcher { margin-left: auto; min-width: 0; max-width: 35%; }
.app-breadcrumbs ol { display: flex; align-items: center; gap: 6px; list-style: none; margin: 0; padding: 3px; min-width: 0; overflow-x: auto; flex: 1; white-space: nowrap; }
.app-breadcrumbs li { display: inline-flex; align-items: center; gap: 6px; flex-shrink: 0; }
.app-breadcrumbs a { color: inherit; text-decoration: none; padding-block: 8px; }
.app-breadcrumbs a:hover { color: var(--cs-accent); text-decoration: underline; }
.app-breadcrumbs li:last-child { color: var(--cs-text); font-weight: 600; }
.app-breadcrumbs a:focus-visible { outline: 2px solid var(--cs-electric); outline-offset: 2px; border-radius: 3px; }
.app-breadcrumbs :deep(.v-btn) { color: var(--cs-accent) !important; height: 36px; text-transform: none; letter-spacing: 0; }
@media(max-width:600px) {
 .app-breadcrumbs { gap: 8px; padding: 4px 12px; }
 .app-breadcrumbs ol { flex-direction: row; }
 .app-breadcrumbs__switcher { max-width: 40px; }
 .app-breadcrumbs__switcher :deep(.season-event-switcher) { min-width: 36px; padding: 0 8px; }
 .app-breadcrumbs__switcher :deep(.v-btn__content), .app-breadcrumbs__switcher :deep(.v-btn__append) { display: none; }
 .app-breadcrumbs__switcher :deep(.v-btn__prepend) { margin: 0; }
}
</style>

<style scoped>
.app-breadcrumbs ol { gap: 8px; }
.app-breadcrumbs li { gap: 8px; }
.app-breadcrumbs li > .v-icon { color: #a78abc; }
.app-breadcrumbs li > [aria-current='page'] { background: var(--cs-selection); color: var(--cs-accent); padding: 6px 10px; border-radius: 7px; font-weight: 650; }
.app-breadcrumbs a { border-radius: 6px; padding-inline: 3px; }
.app-breadcrumbs a:hover { background: #eee0ff; text-decoration: none; }
.app-breadcrumbs__menu :deep(.v-btn) { background: var(--cs-action); color: white !important; border-radius: 8px; width: 36px; }
.app-breadcrumbs__switcher :deep(.v-btn) { background: white; border: 1px solid var(--cs-border); border-radius: 8px; }
</style>

<style scoped>
.app-breadcrumbs__context:empty { display: none; }
.app-breadcrumbs__context { gap: 8px; }
</style>
